import { useCallback, useState } from "react";

import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import { LIVE_LEDGER_ENRICHMENT_PARAMS } from "byzantine/src/TransactionEnrichmentSource";
import { useNotificationContext } from "cerulean";

import {
  extractFilenameFromResponse,
  triggerFileDownload,
} from "../../../../components/DownloadTransactionsDialog/transactionDownloadHelpers";
import { hasFutureDate } from "../helpers";

import type { ExportDownloadParams } from "../constants";
import type { FileFormat } from "../../../../components/DownloadTransactionsDialog/transactionDownloadHelpers";

interface UseTransactionExportOptions {
  accountIds: string[];
  categories?: string[];
  baiCodes?: string[];
  methods?: string[];
  amountMin?: string;
  amountMax?: string;
  settledFrom?: string;
  settledTo?: string;
  type?: string;
  search?: string;
}

interface UseTransactionExportResult {
  downloadExport: (params: ExportDownloadParams) => Promise<boolean>;
  isExporting: boolean;
  isTooLarge: boolean;
}

function resolveFileFormat(params: ExportDownloadParams): string {
  if (params.format === "accounting_software") {
    return params.accountingFormat ?? "qbo";
  }
  return params.format;
}

const DEFAULT_RANGE_DAYS = 30;

function formatLocalDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function isExportTooLargeError(error: unknown): Promise<boolean> {
  if (typeof error !== "object" || error === null) return false;
  const { response } = error as {
    response?: { status?: number; data?: unknown };
  };
  if (response?.status === 422) return true;
  if (response?.status !== 400) return false;

  // With shouldGetBlob, error response bodies arrive as Blobs in production.
  // Read the Blob text to extract the JSON error id.
  let data: unknown = response?.data;
  if (typeof (data as Blob)?.text === "function") {
    try {
      data = JSON.parse(await (data as Blob).text());
    } catch {
      return false;
    }
  }
  return (data as { id?: string })?.id === "date_range_too_large";
}

export const useTransactionExport = ({
  accountIds,
  categories,
  baiCodes,
  methods,
  amountMin,
  amountMax,
  settledFrom,
  settledTo,
  type,
  search,
}: UseTransactionExportOptions): UseTransactionExportResult => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isExporting, setIsExporting] = useState(false);
  const [isTooLarge, setIsTooLarge] = useState(false);

  const downloadExport = useCallback(
    async (params: ExportDownloadParams) => {
      if (isExporting || hasFutureDate(settledFrom, settledTo)) return false;

      setIsExporting(true);
      setIsTooLarge(false);

      const fileFormat = resolveFileFormat(params);

      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - DEFAULT_RANGE_DAYS);
      const effectiveFrom = settledFrom ?? formatLocalDate(thirtyDaysAgo);
      const effectiveTo = settledTo ?? formatLocalDate(today);

      const exportParams: Record<string, string> = {
        file_format: fileFormat,
        settled_from: effectiveFrom,
        settled_to: effectiveTo,
        filter: "hidden",
        ...LIVE_LEDGER_ENRICHMENT_PARAMS,
      };

      if (accountIds.length) {
        exportParams.account_uuids = accountIds.join(",");
      }
      if (categories?.length) exportParams.category = categories.join(",");
      if (baiCodes?.length) exportParams.bai_codes = baiCodes.join(",");
      if (methods?.length) exportParams.method = methods.join(",");
      if (amountMin) exportParams.amount_min = amountMin;
      if (amountMax) exportParams.amount_max = amountMax;
      if (type) exportParams.type = type;
      if (search) exportParams.q = search;

      try {
        const response = await ApiHttp.fetch(
          "transactions/export/",
          { rawResponse: true, shouldGetBlob: true },
          exportParams
        );

        const downloadName =
          params.fileName ||
          extractFilenameFromResponse(
            response.headers["content-disposition"],
            fileFormat as FileFormat
          );

        triggerFileDownload(response.data, downloadName);
        sendNotification({
          type: "success",
          text: l10n.getString("live-ledger-export-success"),
        });
        return true;
      } catch (error) {
        if (await isExportTooLargeError(error)) {
          setIsTooLarge(true);
        } else {
          sendNotification({
            type: "negative",
            text: l10n.getString("live-ledger-export-error"),
          });
        }
        return false;
      } finally {
        setIsExporting(false);
      }
    },
    [
      isExporting,
      accountIds,
      settledFrom,
      settledTo,
      categories,
      baiCodes,
      methods,
      amountMin,
      amountMax,
      type,
      search,
      sendNotification,
      l10n,
    ]
  );

  return { downloadExport, isExporting, isTooLarge };
};
